import { ButtonERP, PrimeReactModal } from "@finanedi.es/storybook"
import { DepartamentoReadingFiltersDtoProps } from "api/DTOs/DepartamentoReadingFiltersDto"
import { UsuarioReadingFiltersDtoIPagedEnumerableProps } from "api/Schemas/UsuarioReadingFiltersDtoIPagedEnumerable"
import {
  GetUsuarioByFiltersParamsProps,
  getUsuarioByFilters,
} from "api/Usuario.service"
import FormikInputText from "common/components/FormikFields/FormikInputText"
import notification from "common/utils/notification"
import { MODAL_STYLES } from "constants/primereactStyles"
import { Formik } from "formik"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import * as Yup from "yup"
import { createDepartamento, updateDepartamento } from "../services/roles"
import MultipleSelectionBox from "./MultipleSelectionBox"

export interface ModalDepartamentosProps {
  isOpen: boolean
  close?: () => void
  refresh: () => void
  rowData?: DepartamentoReadingFiltersDtoProps
}
const ModalDepartamentos = ({
  isOpen,
  refresh,
  rowData,
  close,
}: ModalDepartamentosProps) => {
  const { t } = useTranslation()
  const [listaResponsables, setListaResponsables] = useState<
    UsuarioReadingFiltersDtoIPagedEnumerableProps["items"]
  >([])
  const [listaResponsablesCompleta, setListaResponsablesCompleta] =
    useState<UsuarioReadingFiltersDtoIPagedEnumerableProps>()
  const [listaResponsablesIsLoading, setListaResponsablesIsLoading] =
    useState(false)
  const [queryParams, setQueryParams] =
    useState<GetUsuarioByFiltersParamsProps>({
      SkipCount: 0,
      MaxResultCount: 10,
    })

  const handlePagination = () => {
    if (
      !listaResponsablesIsLoading &&
      Number(queryParams?.SkipCount) <
        Number(listaResponsablesCompleta?.totalCount)
    ) {
      setQueryParams((prevState) => ({
        ...queryParams,
        SkipCount: Number(prevState?.SkipCount) + 10,
      }))
    }
  }

  const fetchUsuarios = async () => {
    try {
      setListaResponsablesIsLoading(true)
      const response = await getUsuarioByFilters({ ...queryParams })
      setListaResponsablesCompleta((prevState) => {
        return {
          ...response,
          ...(prevState?.items &&
            response?.items && {
              items: [...prevState.items, ...response.items],
            }),
        }
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      setListaResponsablesIsLoading(false)
    }
  }

  useEffect(() => {
    isOpen && fetchUsuarios()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.SkipCount, isOpen])

  useEffect(() => {
    if (rowData?.id) {
      getUsuarioByFilters({
        SoloResponsable: true,
      }).then((response) => {
        setListaResponsables(
          response.items?.filter((usuario) =>
            usuario.departamentos?.some(
              (departamento) => departamento.departamentoId === rowData?.id
            )
          )
        )
      })
    }
  }, [rowData?.id])

  return (
    <Formik
      enableReinitialize
      initialValues={{
        nombre: rowData?.nombre,
        responsableNombres: rowData?.responsableNombres,
        ...rowData,
      }}
      validationSchema={Yup.object().shape({
        nombre: Yup.string()
          .trim()
          .required(`${t("errors.invalidName")}`),
      })}
      validateOnBlur={false}
      onSubmit={async (values, { resetForm }) => {
        try {
          rowData?.id
            ? await updateDepartamento(rowData.id, {
                nombre: values.nombre,
                responsablesId: listaResponsables?.map((entry) => entry.id),
              })
            : await createDepartamento(values)
          !rowData?.id
            ? notification.success({
                body: `${t("notification.departamentSuccessCreate")}`,
              })
            : notification.success({
                body: `${t("notification.departamentSuccessUpdate")}`,
              })
          refresh()
          resetForm()
        } catch (err) {
          resetForm()
        }
      }}
    >
      {({ handleSubmit, handleReset }) => (
        <PrimeReactModal
          visible={isOpen}
          closable={false}
          ptContext={MODAL_STYLES}
          className='tw-w-128 tw-rounded-lg tw-py-5 tw-px-6 tw-bg-white'
          baseZIndex={20}
          keepInViewport={false}
          header={
            <div className='tw-text-2xl tw-font-medium tw-text-primary tw-mb-4'>
              {rowData?.id
                ? `${t(
                    "gestionUsuarios.departamentos.table.editDepartament.titleEdit"
                  )}`
                : `${t(
                    "gestionUsuarios.departamentos.table.editDepartament.titleNew"
                  )}`}
            </div>
          }
        >
          <form
            className='tw-w-full tw-flex tw-flex-col tw-text-base tw-font-normal tw-gap-4 tw-text-text'
            name='departamento-form'
          >
            <p>
              {t(
                "gestionUsuarios.departamentos.table.editDepartament.labelDepartament"
              )}
            </p>

            <FormikInputText
              id='nombre'
              name='nombre'
            />

            {rowData?.id && (
              <>
                <p>
                  {t(
                    "gestionUsuarios.departamentos.table.editDepartament.labelResponsible"
                  )}
                </p>
                <MultipleSelectionBox
                  options={listaResponsablesCompleta?.items}
                  handler={setListaResponsables}
                  list={listaResponsables}
                  handlePagination={handlePagination}
                />
              </>
            )}
          </form>
          <div
            key='actions'
            className='tw-pb-1 tw-flex tw-flex-col sm:tw-flex-row tw-justify-end tw-pt-4 tw-gap-4'
          >
            <ButtonERP
              customize={{
                type: "transparent",
                size: "md",
              }}
              label={`${t(
                "gestionUsuarios.departamentos.table.editDepartament.buttonCancel"
              )}`}
              onClick={(e) => {
                handleReset(e)
                close?.()
              }}
            />
            <ButtonERP
              customize={{
                type: "primary",
                size: "md",
              }}
              label={
                rowData?.id
                  ? `${t(
                      "gestionUsuarios.departamentos.table.editDepartament.buttonSave"
                    )}`
                  : `${t("gestionUsuarios.departamentos.createDepartament")}`
              }
              onClick={() => handleSubmit()}
            />
          </div>
        </PrimeReactModal>
      )}
    </Formik>
  )
}

export default ModalDepartamentos
